import React, { FC, useState, useEffect } from 'react';
import { Select, MenuItem, SelectChangeEvent, FormControl, InputLabel, Box } from '@mui/material';
import { CONSTANTS } from 'config/constants';
import { useTranslation } from 'react-i18next';

type TimeMinuteProps = {
    onTimeChange: (hour?: number, minute?: number) => void;
    hour?: number;
    minute?: number;
    showFullHourMinute?: boolean;
    dataTest?: string;
};

const TimeMinute: FC<TimeMinuteProps> = ({
    onTimeChange,
    hour: propHour,
    minute: propMinute,
    showFullHourMinute,
    dataTest,
}: TimeMinuteProps) => {
    const { t: translate } = useTranslation(['common']);
    const currentHour = new Date().getHours();
    let currentMinute = new Date().getMinutes();
    currentMinute = currentMinute - (currentMinute % 5);
    const [hour, setHour] = useState<number | undefined>(propHour || 0);
    const [minute, setMinute] = useState<number | undefined>(propMinute || 0);

    useEffect(() => {
        setHour(propHour);
        setMinute(propMinute);
    }, [propHour, propMinute]);

    const handleHourChange = (event: SelectChangeEvent<number>) => {
        const value = event.target.value as number;
        onTimeChange(value, minute);
        setHour(value);
        setMinute(0);
    };

    const handleMinuteChange = (event: SelectChangeEvent<number>) => {
        const value = event.target.value as number;
        onTimeChange(hour, value);
        setMinute(value);
    };

    return (
        <Box display="flex">
            <Box mr={1}>
                <FormControl sx={{ minWidth: 90 }} margin="dense">
                    <InputLabel id="hours-label">{translate('common:HOUR')}</InputLabel>
                    <Select
                        labelId="hours-label"
                        label={translate('common:HOUR')}
                        value={hour}
                        onChange={handleHourChange}
                        data-test={`${dataTest}.hour-select`}
                    >
                        <MenuItem disabled>{translate('common:HOUR')}</MenuItem>
                        {CONSTANTS.HOURS.map((hour, index) => (
                            <MenuItem
                                disabled={!showFullHourMinute && parseInt(hour) > currentHour}
                                value={parseInt(hour)}
                                key={index}
                            >
                                {hour}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <FormControl sx={{ minWidth: 90 }} margin="dense">
                    <InputLabel id="minutes-label">{translate('common:MINUTE')}</InputLabel>
                    <Select
                        labelId="minutes-label"
                        label={translate('common:MINUTE')}
                        value={minute}
                        onChange={handleMinuteChange}
                        disabled={typeof hour !== 'number'}
                        data-test={`${dataTest}.minute-select`}
                    >
                        <MenuItem disabled>{translate('common:MINUTE')}</MenuItem>
                        {CONSTANTS.MINUTES.map((minute, index) => (
                            <MenuItem
                                disabled={
                                    !showFullHourMinute &&
                                    !(hour && currentHour > hour) &&
                                    parseInt(minute) > currentMinute
                                }
                                value={parseInt(minute)}
                                key={index}
                            >
                                {minute}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
};

export default TimeMinute;
